import type { Money, WalletTransaction } from "./types.js";

export const COPPER_PER_SILVER = 100;
export const SILVER_PER_GOLD = 10;
export const COPPER_PER_GOLD = COPPER_PER_SILVER * SILVER_PER_GOLD;

export interface MoneyDisplay {
  gold: number;
  silver: number;
  copper: number;
  totalCopper: number;
}

export function normalizeMoney(value: Money | number): MoneyDisplay {
  const totalCopper = typeof value === "number" ? value : value.copper;
  if (!Number.isSafeInteger(totalCopper) || totalCopper < 0) {
    throw new Error("Coin balance must be a nonnegative integer");
  }
  const gold = Math.floor(totalCopper / COPPER_PER_GOLD);
  const afterGold = totalCopper % COPPER_PER_GOLD;
  return {
    gold,
    silver: Math.floor(afterGold / COPPER_PER_SILVER),
    copper: afterGold % COPPER_PER_SILVER,
    totalCopper
  };
}

export function formatMoney(value: Money | number): string {
  const { gold, silver, copper } = normalizeMoney(value);
  const pieces: string[] = [];
  if (gold) pieces.push(`${gold} gold`);
  if (silver) pieces.push(`${silver} silver`);
  if (copper || pieces.length === 0) pieces.push(`${copper} copper`);
  return pieces.join("  ");
}

export function createWalletTransaction(input: {
  id: string;
  userId: string;
  currentBalance: number;
  amountCopper: number;
  reason: string;
  idempotencyKey: string;
  createdAt?: string;
}): WalletTransaction {
  if (!Number.isSafeInteger(input.amountCopper)) throw new Error("Coin amount must be an integer");
  const balanceAfter = input.currentBalance + input.amountCopper;
  if (balanceAfter < 0) throw new Error("Not enough coins");
  return {
    id: input.id,
    userId: input.userId,
    amountCopper: input.amountCopper,
    balanceAfter,
    reason: input.reason,
    idempotencyKey: input.idempotencyKey,
    createdAt: input.createdAt ?? new Date().toISOString()
  };
}

export function canAfford(balance: Money | number, priceCopper: number): boolean {
  return (typeof balance === "number" ? balance : balance.copper) >= priceCopper;
}
